<script setup lang="ts">
import type { Entitlement, MonospacePolicyEntitlementResource, PolicyEntitlementAction } from '#layers/access';
import type { BlockRootContext } from '#layers/block-engine';
import type { AnyItem, CompositePrimaryKey, Filter, GlobalCollectionId } from '#layers/data-model';
import type { FormBlockSuccessEventPayload } from '~/composables/form-block/types';
import { useOverlay } from '@nuxt/ui/composables';
import { cloneDeep } from 'lodash-es';
import { computed, defineAsyncComponent, ref } from 'vue';
import { useAppConfig } from '#app/config';
import { useStore } from '#imports';
import { entitlementKeyMatchesAction } from '#layers/access';
import { PageSlotProvider, TeleportPageActions } from '#layers/chrome';
import { ButtonAdd, ListCard } from '#layers/ui';
import { usePolicyEntitlements } from '~/composables/policy';
import { usePolicyEntitlementTranslations } from '~/composables/policy/use-policy-entitlement-translations';
import { useProjectSchemaStore } from '~/stores/project-schema';

interface PolicyCustomSettingsDrawerProps {
	collectionId: GlobalCollectionId;
	title?: string;
	itemId?: CompositePrimaryKey;
	itemEdits?: AnyItem;
	formMode?: 'submit' | 'emit';
	entitlements: Entitlement[];
	resource: MonospacePolicyEntitlementResource;
	action: PolicyEntitlementAction;
}
const props = defineProps<PolicyCustomSettingsDrawerProps>();
const emit = defineEmits<{
	close: [payload?: FormBlockSuccessEventPayload];
}>();

const LazyCollectionListingDrawer = defineAsyncComponent(() => import('~/components/Collection/CollectionListingDrawer.vue'));

const appConfig = useAppConfig();
const { findCollectionById } = useProjectSchemaStore();

const collection = computed(() => findCollectionById(props.collectionId));
const blockContext = computed<BlockRootContext>(() => ({
	itemId: props.itemId,
	formMode: props.formMode,
	itemEdits: props.itemEdits,
	pageType: 'detail',
	pageIsFullWidth: false,
	pageDisplayMode: 'drawer',
}));

const { entitlementName, entitlementDescription, resourceTitlePlural, resourceTitleSingular } = usePolicyEntitlementTranslations({
	resource: () => props.resource,
	action: () => props.action,
});

const store = useStore();
const entitlements = ref<Entitlement[]>(cloneDeep(props.entitlements?.filter((entitlement) => entitlementKeyMatchesAction(entitlement, { resource: props.resource.name, action: props.action.name })) ?? []));
const {
	addEntitlementWildcard,
	removeEntitlement,
	hasWildcardEntitlement,
	selectedResourceIdsForEntitlement,
	removeResourceSelection,
	addResourceSelection,
} = usePolicyEntitlements({ entitlements });

const { data: selectedResources } = store.$collection(collection.value?.apiName ?? '').query((q) => {
	if (hasWildcardEntitlement(props.resource, props.action)) {
		return q.many();
	}

	return q.many({ filter: { id: { _in: selectedResourceIdsForEntitlement(props.resource, props.action) } } });
});

function toggleEntitlement() {
	if (hasWildcardEntitlement(props.resource, props.action)) {
		removeEntitlement(props.resource, props.action);
	}
	else {
		addEntitlementWildcard(props.resource, props.action);
	}
}

function save() {
	emit('close', {
		key: props.itemId!,
		data: {
			entitlements: entitlements.value,
			resource: props.resource,
			action: props.action,
		},
	});
}

async function handleAddResource() {
	const filter: Filter = {
		id: { _nin: selectedResourceIdsForEntitlement(props.resource, props.action) },
	};

	const modal = useOverlay().create(LazyCollectionListingDrawer, {
		props: {
			collectionId: props.collectionId,
			selectionMode: 'multiple',
			filter,
		},
	});

	const result = await modal.open().result as { selection: CompositePrimaryKey[] } | undefined;

	if (result) {
		for (const item of result.selection) {
			addResourceSelection(props.resource, props.action, item.id as GlobalCollectionId);
		}
	}
}
</script>

<template>
	<DrawerPage
		:attach-to-route="false"
		:ui="{ container: 'gap-0 2xl:w-200', body: 'p-0' }"
		:title="entitlementName"
		:description="$t('common.select_thing', { thing: resourceTitleSingular })"
		@close="() => emit('close')"
	>
		<PageSlotProvider>
			<PageShell :context="blockContext">
				<div class="flex flex-col items-stretch gap-6">
					<DNotice
						v-if="entitlementDescription"
						color="neutral"
						:description="entitlementDescription"
					/>
					<DForm
						layout="side-by-side"
						layout-split="1-1"
						:state="{ hasWildcardEntitlement: hasWildcardEntitlement(resource, action) }"
					>
						<DFormField
							:label="$t('common.all_things', { things: resourceTitlePlural })"
							:description="$t('policy.entitlements.enable_all_resources', { resources: resourceTitlePlural })"
						>
							<DSwitch
								:model-value="hasWildcardEntitlement(resource, action)"
								@click="toggleEntitlement"
							/>
						</DFormField>
					</DForm>
					<ListCard v-if="!hasWildcardEntitlement(resource, action)" :items="selectedResources">
						<template #header>
							<span class="text-xs font-medium text-toned">{{ $t('common.n_items', selectedResources.length) }}</span>
						</template>
						<template #header-actions>
							<div class="-me-2 flex items-center gap-2">
								<ButtonAdd size="sm" variant="solid" color="neutral" :label="$t('common.add_item')" :icon="appConfig.ui.icons.plus" @click="handleAddResource" />
							</div>
						</template>
						<template #item-content="{ item }">
							<div class="flex flex-1 items-center gap-2">
								<RenderCollectionDisplayTemplate
									:key="item.id"
									class="flex-1 text-sm"
									:item
									:collection-id
								/>
								<DButton size="sm" variant="link" color="neutral" icon="lucide:x" @click="removeResourceSelection(resource, action, item.id)" />
							</div>
						</template>
					</ListCard>
				</div>
			</PageShell>
			<TeleportPageActions>
				<DButton color="neutral" variant="outline" :label="$t('common.cancel')" @click="emit('close')" />
				<DButton color="primary" :label="$t('common.save')" @click="save" />
			</TeleportPageActions>
		</PageSlotProvider>
	</DrawerPage>
</template>
